import React from "react";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import EwaybillType from "@/enum/EwaybillType";
import LogisticsCompany from "@/enum/LogisticsCompany";
import EwaybillTypeNameMap from "@/map/EwaybillTypeNameMap";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { Tooltip } from "antd";
import moment from "moment";
import { TbLogisticsCompanies } from "./LogisticsCompanyUtil";
import LogisticsCompanyTypeMap from "@/map/LogisticsCompanyTypeMap";
import LogisticsCompanyType from "@/enum/LogisticsCompanyType";
import ShopType from "@/enum/ShopType";

export function buildEwaybillAccountInfo(
    ewaybillAccount: Pick<EwaybillAccountDTO, "platformUsername" | "shopId" | "type" | "name">,
    shopMapping: Map<number, ShopDTO>,
) {
    if (!ewaybillAccount) return {};

    const { platformUsername, shopId, type, name } = ewaybillAccount;

    /** 优先使用name，无name则判断是否为平台的电子面单账号，如果是，展示店铺名称，最后再使用platformUsername */
    const isPlatFormType = [
        EwaybillType.TAOBAO_CAINIAO,
        EwaybillType.JD,
        EwaybillType.PDD,
        EwaybillType.JINRITEMAI,
        EwaybillType.KWAISHOP,
        EwaybillType.JINRITEMAI_IOP,
        EwaybillType.JINRITEMAI_SUPPLIER,
        EwaybillType.KWAISHOP_DROPSHIP,
        EwaybillType.CHANNEL_SHOP,
        EwaybillType.CHANNEL_SHOP_DROPSHIP,
        EwaybillType.XIAOHONGSHU,
    ].includes(type);

    let shopName = isPlatFormType ? shopMapping.get(shopId)?.name : null;

    return {
        name: name ?? shopName ?? platformUsername,

        ewaybillTypeName: EwaybillTypeNameMap[type],
    };
}

/** 获取最新日期同一batchNo的运单号(需要用ewaybill detail) */
export function getLatestSameBatchWaybillNosByOrders(orders: OrderDTO[]) {
    let latestDateCreated = "";

    let latestDateCreatedAndSameBatchNoWaybills: EwaybillDTO[] = [];

    let batchNo = null;

    orders.forEach(({ ewaybills }) => {
        if (ewaybills?.length) {
            ewaybills.forEach((ewaybill) => {
                if (!latestDateCreated) {
                    latestDateCreated = ewaybill.dateCreated;

                    latestDateCreatedAndSameBatchNoWaybills.push(ewaybill);

                    batchNo = ewaybill.details?.batchNo;
                } else if (moment(latestDateCreated).isBefore(moment(ewaybill.dateCreated))) {
                    latestDateCreated = ewaybill.dateCreated;

                    batchNo = ewaybill.details?.batchNo;

                    latestDateCreatedAndSameBatchNoWaybills = [ewaybill];
                } else if (
                    moment(latestDateCreated).isSame(moment(ewaybill.dateCreated)) &&
                    ewaybill.details?.batchNo === batchNo
                ) {
                    latestDateCreatedAndSameBatchNoWaybills.push(ewaybill);
                }
            });
        }
    });

    return latestDateCreatedAndSameBatchNoWaybills;
}

export function getLatestSameBatchWaybillNosByEwaybills(ewaybills: EwaybillDTO[] = []) {
    let latestDateCreated = "";

    let latestDateCreatedAndSameBatchNoWaybills: EwaybillDTO[] = [];

    let batchNo = null;

    ewaybills.forEach((ewaybill) => {
        if (ewaybill.dateCreated) {
            if (!latestDateCreated) {
                latestDateCreated = ewaybill.dateCreated;

                latestDateCreatedAndSameBatchNoWaybills.push(ewaybill);

                batchNo = ewaybill.details?.batchNo;
            } else if (moment(latestDateCreated).isBefore(moment(ewaybill.dateCreated))) {
                latestDateCreated = ewaybill.dateCreated;

                batchNo = ewaybill.details?.batchNo;

                latestDateCreatedAndSameBatchNoWaybills = [ewaybill];
            } else if (
                moment(latestDateCreated).isSame(moment(ewaybill.dateCreated)) &&
                ewaybill.details?.batchNo === batchNo
            ) {
                latestDateCreatedAndSameBatchNoWaybills.push(ewaybill);
            }
        }
    });

    return latestDateCreatedAndSameBatchNoWaybills;
}

//  淘系套壳快递 和抖店官方代发快递（DOUDYIN_EXPRESS）需从extraInfo中获取realLogisticsCompany进行'展示'
export function getEwaybillRealLogisticsCompany(ewaybill: EwaybillDTO) {
    if (!ewaybill) return undefined;

    if (![...TbLogisticsCompanies, LogisticsCompany.DOUYIN_EXPRESS].includes(ewaybill.logisticsCompany)) {
        return ewaybill.logisticsCompany;
    }

    let realLogisticsCompany: LogisticsCompany = ewaybill.logisticsCompany;

    try {
        realLogisticsCompany = JSON.parse(ewaybill.extraInfo).realLogisticsCompany;
    } catch {}

    return realLogisticsCompany;
}

export function getEwaybillRealLogisticsCompanyNode(ewaybill: EwaybillDTO) {
    if (!ewaybill) return null;

    const realLogisticsCompany = getEwaybillRealLogisticsCompany(ewaybill);

    /** 抖音在线寄件 */
    if (ewaybill.type === EwaybillType.JINRITEMAI_ONLINE_SHIP) {
        return (
            <span>
                <Tooltip title="抖音在线寄件">
                    <span
                        style={{
                            display: "inline-block",
                            width: 15,
                            height: 15,
                            fontSize: 11,
                            textAlign: "center",
                            lineHeight: "15px",
                            backgroundColor: "#000",
                            color: "#fff",
                            borderRadius: 4,
                            marginRight: 3,
                        }}
                    >
                        抖
                    </span>
                </Tooltip>

                {LogisticsCompanyNameMap[realLogisticsCompany]}
            </span>
        );
    }

    /** 避免ExtraInfo中不存在logisticsCompany，需多加一层真实快递公司和解析出来的快递公司是否相等，如果不相同，说明ExtraInfo中无快递公司，走默认显示即可 */

    /** 抖音官方快递展示 */
    if (
        ewaybill.logisticsCompany === LogisticsCompany.DOUYIN_EXPRESS &&
        realLogisticsCompany !== ewaybill.logisticsCompany
    ) {
        return (
            <span>
                <Tooltip title="抖音官方代发快递">
                    <img
                        style={{
                            width: 14,
                            height: 14,
                            marginRight: 3,
                        }}
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAACUUlEQVR4Ac2XAYQUURzG/xAENwiCvSEI4hwkjiYIgg3CpWtBJGkFwmUHIJzbBKRrF1IokxUhGQQRlg4INxLo6np1bd21s7tf+03t45nbttnXzvbxGbvM//u99977PyO/5fYd9q36Rg4OmKnDdXC+VgOIwKpQ+7Pp69eyvB8SALYAQa+NqNf95dpTBHI0yyzYA4S9GFqqBeWcyVDjXwNQqw14cmB6AFRYuTVdAKpSW4PMFqYHQHGDuh/e8XRMBUCrWWvwdOQAEL3Hnqo9B6Q4JoAzAzm+AFlahFy+yHUdDuDfhyqtaBA7AAZz7TbfJkFO+wvm4m0Uly5hTmaGAoSygJIUEHlXgStrDOczIwBH+eZ1EuDFX82Q0k3UZP5PALoO+0FVjiT/NeVEOmdU+Gp3F1rZACz6gBmeM8C5xaRoqfPd6O/wHwDOWa4jnxMEeHgvKcqbTctbZnBSuCgH4cp+ejIA3PHFuAWtcJ3hesSGJwHAgtXOrlGQAHrEpiczA/WtTWhVG8MLzhbSAOU7lgCvXqDe/WFswMg5vdeL7IppAG/ZDoCdL1kCs42mloCtmbPltrcNWH1CxgXgtHpxK32bRRtwV24kgPLsiW7N4aePqX5/XgpjA+ipbaqtFASPpt/ZQSn+hkp3h7/NW9C9gEhOsoYdAO3dvY2/lmox3By9LQDt+z5GKlzX4b4cNmvYAtBl9xhU9RHQ3IBSCnq6g5e6Q9JlOTROOD36s4wnoC7zXF8dSCs5xatWn5AxLWGWFxzZZ9wFln78X3ycDiCCHIPDQfhPEXLTs1r7sGQAAAAASUVORK5CYII="
                    />
                </Tooltip>

                {LogisticsCompanyNameMap[realLogisticsCompany]}
            </span>
        );
    }

    /** 淘系套壳快递展示 */
    if (
        TbLogisticsCompanies.includes(ewaybill.logisticsCompany) &&
        ewaybill.logisticsCompany !== realLogisticsCompany
    ) {
        return (
            <span>
                <Tooltip title={`淘系物流:${LogisticsCompanyNameMap[ewaybill.logisticsCompany]}`}>
                    <svg
                        className="icon"
                        viewBox="0 0 1024 1024"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        p-id="8431"
                        width="14"
                        height="14"
                    >
                        <path
                            d="M0 0m128 0l768 0q128 0 128 128l0 768q0 128-128 128l-768 0q-128 0-128-128l0-768q0-128 128-128Z"
                            fill="#0366FE"
                            p-id="8432"
                        ></path>
                        <path
                            d="M508.224 128c15.456 0 30.88 0.832 46.24 2.464 9.696 1.024 19.136 2.336 27.936 4.032 48.576 8.96 81.536 26.72 81.536 46.944a13.632 13.632 0 0 1 0 2.336v2.08c96.032 42.016 159.232 133.984 189.344 298.336 37.76 35.392 48.864 102.976 39.68 160.064-8.768 60.448-53.888 97.408-109.984 104.96-37.856 36.576-93.952 67.04-176.16 81.568v33.728c20.064 10.528 37.12 26.016 49.504 45.024 0 5.568-21.312 9.984-47.552 9.984s-47.552-4.544-47.552-9.984c0.192-12.096 2.816-24 7.744-35.04v-38.912c-17.44 1.92-35.808 3.648-55.328 3.648h-5.152c-16-0.416-31.296-1.44-45.888-2.752v37.632c4.896 11.04 7.552 22.944 7.744 35.008 0 5.6-21.312 9.984-47.552 9.984s-47.552-4.512-47.552-9.984a132.8 132.8 0 0 1 49.504-44.992v-32.16c-87.648-13.888-147.104-45.44-185.856-83.552-55.328-8.16-99.648-44.736-108.32-104.672-8.128-56.16 2.592-122.336 38.784-158.272 28.8-162.24 90.72-254.368 183.904-297.152l-0.416-1.344-0.352-1.376a19.616 19.616 0 0 1 0-4.16v-1.12C354.304 151.36 423.264 128 508.256 128z m1.152 80.544a643.776 643.776 0 0 0-120.32 8.704l-5.888 1.216a258.016 258.016 0 0 0-23.2 6.176h-0.896c-8.864 2.88-17.504 6.464-25.856 10.624l-3.68 2.016a263.264 263.264 0 0 0-7.296 4.224l-1.44 0.768c-7.392 4.672-14.368 10.016-20.8 15.968l-1.664 1.536a167.68 167.68 0 0 0-8.288 8.704l-0.896 1.024a133.952 133.952 0 0 0-8.416 10.784l-1.152 1.696c-2.24 3.2-4.352 6.56-6.336 10.112l-1.536 2.72c-4.768 8.768-8.768 17.92-12.032 27.36l-1.28 3.776c-1.376 4.16-2.688 8.48-3.872 12.96l-0.928 3.104c-1.376 5.44-2.624 11.136-3.744 16.992-0.64 1.184-0.64 2.624-0.64 3.904-0.768 4.832-1.504 9.824-2.208 14.912-0.64 1.696-0.64 3.52-0.64 5.312-0.768 6.624-1.792 13.248-1.792 20.256v39.68c0 91.712 89.536 147.616 207.68 162.144l59.84-46.304 59.968 46.304c118.112-14.528 207.68-70.432 207.68-162.144l-1.28-40.608a301.632 301.632 0 0 0-1.824-20.48c-0.64-1.824-0.64-3.52-0.64-5.312-0.704-5.376-1.472-10.56-2.336-15.584v-3.36a212 212 0 0 0-3.744-17.12l-1.024-3.776a235.104 235.104 0 0 0-3.872-12.96l-1.184-3.648a185.344 185.344 0 0 0-5.664-14.4l-0.8-1.536a249.92 249.92 0 0 0-5.92-11.68l-1.696-2.976a145.92 145.92 0 0 0-7.36-11.552l-2.08-2.88a124.32 124.32 0 0 0-6.464-7.904l-1.792-1.92a189.76 189.76 0 0 0-8.288-8.32l-1.152-1.152a189.504 189.504 0 0 0-10.336-8.448l-1.152-0.768a125.28 125.28 0 0 0-10.112-6.72l-1.536-1.056a169.664 169.664 0 0 0-24.704-11.936h-1.536a229.12 229.12 0 0 0-28.032-8.576h-0.8c-4.736-1.12-9.6-2.144-14.592-3.104l-6.144-1.024c-6.208-0.96-12.544-1.824-18.912-2.592h-2.72c-8.8-0.928-17.216-2.08-25.856-2.08h-4.256c-8.64-0.704-17.248-1.056-25.856-1.056h-36.704z m2.688 138.88a109.632 109.632 0 0 1 109.6 109.28c0.64 8.32-3.488 16.32-10.624 20.672a22.464 22.464 0 0 1-23.36 0 22.272 22.272 0 0 1-10.592-20.672A64.96 64.96 0 0 0 512 391.872a64.96 64.96 0 0 0-65.088 64.832c0.64 8.32-3.456 16.32-10.624 20.672a22.464 22.464 0 0 1-23.296 0 22.272 22.272 0 0 1-10.656-20.672 109.632 109.632 0 0 1 109.728-109.28z"
                            fill="#FFFFFF"
                            p-id="8433"
                        ></path>
                    </svg>
                </Tooltip>
                <span className="ml2">{LogisticsCompanyNameMap[realLogisticsCompany]}</span>
            </span>
        );
    }

    return LogisticsCompanyNameMap[ewaybill.logisticsCompany];
}

export const shareEwaybillTypes = [
    EwaybillType.TAOBAO_CAINIAO_AUTH_SHARE,
    EwaybillType.JD_SHARE,
    EwaybillType.OFFICIAL_SHARE,
    EwaybillType.PDD_SHARE,
    EwaybillType.PDD_AUTH_SHARE,
    EwaybillType.JINRITEMAI_AUTH_SHARE,
    EwaybillType.JINRITEMAI_SHARE,
    EwaybillType.KWAISHOP_SHARE,
    EwaybillType.CHANNEL_SHOP_SHARE,
    EwaybillType.XIAOHONGSHU_SHARE,
];

export function isShareEwaybillAccount(ewaybillAccount: EwaybillAccountDTO) {
    if (!ewaybillAccount) return false;

    return shareEwaybillTypes.includes(ewaybillAccount.type);
}

/**
 * 是否显示账户余额
 */
export function shouldShowBranchAccountQuantity(
    ewaybillType: EwaybillType,
    logisticsCompany: LogisticsCompany,
    quantity?: number,
) {
    // 如果 quantity 不是数字，直接不展示额度。
    if (typeof quantity !== "number") {
        return false;
    }

    // 非平台单类型（即官方、京东）
    const isPlatformType = ![EwaybillType.OFFICIAL, EwaybillType.JDL].includes(ewaybillType);
    const companyType = LogisticsCompanyTypeMap[logisticsCompany];

    // 平台单 + 直营公司
    if (isPlatformType && companyType === LogisticsCompanyType.DIRECT) {
        if (shareEwaybillTypes.includes(ewaybillType)) {
            // 共享类型，则可以展示，代表的是共享的余额
            return true;
        } else {
            return false;
        }
    }

    // 平台单 + 直营和加盟混合公司，且额度小于等于 0，不展示额度。
    if (isPlatformType && companyType === LogisticsCompanyType.DIRECT_AND_FRANCHISE_MIXED && quantity <= 0) {
        return false;
    }

    // 其他情况展示额度。
    return true;
}

export const BackendBlockExpressUrlMap = {
    [ShopType.TAOBAO]:
        "https://helpcenter.taobao.com/servicehall/knowledge_detail?kwdContentId=528573082430003200&searchKey=6863d8ecf6404a8895386fb5eeefafca1&sessionId=01c4c12077494fe3acbcb0830ba6e031&source=6&external=true",
    [ShopType.PINDUODUO]: "https://mms.pinduoduo.com/orders/expressInterception/activate",
    [ShopType.KWAI]: "https://s.kwaixiaodian.com/zone/supplychain/express-service/package-intercept",
    [ShopType.DOUDIAN]: "https://fxg.jinritemai.com/ffa/logistics/logistics/interceptModule",
    [ShopType.CHANNEL_SHOP]: "https://store.weixin.qq.com/chengzhang/article/wiki?docid=2103&nonce=efa99bbc69921700",
    [ShopType.ALIBABA]: "https://peixun.1688.com/space/l2AmoYb53knMgzdb/detail/r1R7q3QmWe7MZ2vvHoenM6AnJxkXOEP2",
};
